import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
      // Live Plotly/canvas mutation streams are the replay buffer's worst case
      // on this dashboard — they grow session RAM into the hundreds of MB.
      block: ['.js-plotly-plot', 'canvas'],
    }),
  ],
  tracesSampleRate: 0.1,
  // Session replay on a live-updating tape records every DOM mutation. Keep
  // error replays; skip the 5% background sample that made sessions laggy.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
})

function AppCrashFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#0A0A0A] text-white">
      <div className="text-center">
        <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
        <p className="text-gray-400 mb-4 text-sm">The error has been reported automatically.</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="text-blue-400 underline text-sm"
          >
            Reload the page
          </button>
          <a
            href="/diagnostics"
            className="text-blue-400 underline text-sm"
          >
            Run connection diagnostics
          </a>
        </div>
      </div>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)

// Register inert installability SW (public/sw.js). Network-only fetch —
// never cache market UI. Also clears any leftover Workbox caches on activate.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Best-effort: SW disabled / policy still leaves the SPA usable.
    });
  });
}

// Vite emits this event when a dynamic import() fails to load its preload chunk —
// almost always because the user's index.html references a hash that no longer
// exists post-deploy. One forced reload picks up the fresh index.html and clears
// the problem. Guard with sessionStorage so we don't infinite-loop if reload
// doesn't actually fix it (e.g., genuine 500 from origin).
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem('chunkReloadAttempted') === '1') {
    // Already tried reloading once this session — let the error surface to Sentry
    // rather than burn cycles in a reload loop.
    return;
  }
  sessionStorage.setItem('chunkReloadAttempted', '1');
  event.preventDefault();
  window.location.reload();
});
